import { DISCOUNT_TIERS, PRINT_ADS, DIGITAL_ADS, SPECIAL_EDITIONS, PROMOS } from '../config';

export function getDiscountPerWeek(weeks) {
  for (const tier of DISCOUNT_TIERS) {
    if (weeks >= tier.minWeeks) return tier.discountPerWeek;
  }
  return 0;
}

export function getPrintAd(id) {
  return PRINT_ADS.find(a => a.id === id) || null;
}

// 'YYYY-MM-DD' -> local-midnight Date (no UTC shift).
function parseYmd(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// The one issue a print size runs in (a one-issue special like the Season of
// Giving spread), or null for every regular size.
export function getOnlyIssue(printAdId) {
  const ad = getPrintAd(printAdId);
  return ad && ad.onlyIssue ? parseYmd(ad.onlyIssue) : null;
}

// Whether to offer a print size to this advertiser right now: non-profit-only
// sizes stay hidden from businesses, and a one-issue special disappears once
// ordering for its issue has closed.
export function isPrintAdOffered(ad, orgType, now = new Date()) {
  if (!ad) return false;
  if (ad.nonprofitOnly && orgType !== 'nonprofit') return false;
  if (ad.onlyIssue && !isIssueOpen(parseYmd(ad.onlyIssue), now)) return false;
  return true;
}

export function getDigitalAd(id) {
  return DIGITAL_ADS.find(a => a.id === id) || null;
}

export function getDigitalAdPrice(id, hasPrint, isNonprofit = false) {
  const ad = getDigitalAd(id);
  if (!ad) return 0;
  if (isNonprofit) {
    return hasPrint ? ad.nonprofitAddonPrice : ad.nonprofitStandalonePrice;
  }
  return hasPrint ? ad.addonPrice : ad.standalonePrice;
}

// Returns the correct print price for a given ad based on org type.
export function getPrintAdPrice(ad, isNonprofit = false) {
  if (!ad) return 0;
  return isNonprofit ? (ad.nonprofitPrice || ad.price) : ad.price;
}

// Looks up a promo code from config, or null if it doesn't exist or has expired.
export function findPromo(code) {
  if (!code) return null;
  const promo = PROMOS.find(p => p.code.toLowerCase() === String(code).toLowerCase());
  if (!promo) return null;
  // YYYY-MM-DD strings compare chronologically as plain strings, which sidesteps
  // the UTC-offset shift that parsing these into Dates would introduce.
  if (promo.expires && toDateInputValue(new Date()) > promo.expires) return null;
  return promo;
}

// Why a promo is (or isn't) discounting this order. The `reason` drives the
// on-screen explanation, so an advertiser who followed a sale link is never left
// wondering why the price didn't move.
export function getPromoStatus(form) {
  const promo = findPromo(form.promo);
  if (!promo) return { promo: null, applies: false, reason: 'none' };

  const hasPrint = form.medium === 'print' || form.medium === 'both';
  const printAd  = hasPrint && form.printAd ? getPrintAd(form.printAd) : null;

  if (!hasPrint)       return { promo, applies: false, reason: 'no_print'     };
  if (!printAd)        return { promo, applies: false, reason: 'no_selection' };
  if (!promo.eligiblePrintAds.includes(printAd.id))
                       return { promo, applies: false, reason: 'wrong_size'   };
  if (!form.startDate) return { promo, applies: false, reason: 'no_date'      };
  if (!promo.startDates.includes(toDateInputValue(form.startDate)))
                       return { promo, applies: false, reason: 'wrong_date'   };

  return { promo, applies: true, reason: 'ok' };
}

// Human-readable list of the Wednesdays a promo will start on, e.g. "Aug 5 or Aug 12".
export function formatPromoStartDates(promo) {
  if (!promo) return '';
  const labels = promo.startDates.map(ymd => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

// One-line explanation of why an active promo isn't discounting this order yet.
// Returns null when there's nothing useful to say — either no promo is running,
// or it's already applied and the price speaks for itself.
export function getPromoHint(pricing) {
  const { promo, promoApplies, promoReason } = pricing;
  if (!promo || promoApplies) return null;
  const save = `save ${fmtDollars(promo.discountPerWeek)} a week`;
  switch (promoReason) {
    case 'no_print':
    case 'no_selection':
      return `${promo.label} applies to ${formatPromoSizes(promo)} print ads — add one to ${save}.`;
    case 'wrong_size':
      return `${promo.label} applies to ${formatPromoSizes(promo)} — go back and size up to ${save}.`;
    case 'wrong_date':
      return `${promo.label} applies to runs starting ${formatPromoStartDates(promo)} — pick one of those Wednesdays to ${save}.`;
    default:
      return null;
  }
}

// Human-readable list of the sizes a promo covers, e.g. "Quarter Page, Half Page or Full Page".
export function formatPromoSizes(promo) {
  if (!promo) return '';
  const labels = promo.eligiblePrintAds
    .map(id => getPrintAd(id))
    .filter(Boolean)
    .map(a => a.label);
  if (labels.length <= 1) return labels[0] || '';
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

export function calcPricing(form) {
  const hasPrint   = form.medium === 'print'   || form.medium === 'both';
  const hasDigital = form.medium === 'digital'  || form.medium === 'both';
  const weeks      = form.weeks || 1;
  const isNonprofit = form.orgType === 'nonprofit';

  const printAd     = hasPrint && form.printAd ? getPrintAd(form.printAd) : null;
  const printBase   = printAd ? getPrintAdPrice(printAd, isNonprofit) : 0;
  const printWeekly = printBase;            // nominal weekly print rate (discount applied order-wide below)
  const printCost   = printWeekly * weeks;  // print subtotal before discount

  // Digital cost is computed per ad, since a placement can skip individual
  // weeks (e.g. when a slot is sold out for one Wednesday). A skip only counts
  // if it falls within the current run schedule.
  const scheduleKeys = getRunSchedule(form.startDate, weeks).map(toDateInputValue);
  const skips        = form.digitalSkips || {};
  const digitalLines = [];
  let digitalCost = 0;
  if (hasDigital) {
    (form.digitalAds || []).forEach(id => {
      const unit    = getDigitalAdPrice(id, hasPrint, isNonprofit);
      const skipped = (skips[id] || []).filter(d => scheduleKeys.includes(d)).length;
      const adWeeks = Math.max(0, weeks - skipped);
      digitalCost  += unit * adWeeks;
      digitalLines.push({ id, unit, weeks: adWeeks, skipped });
    });
  }
  const digitalWeekly = digitalLines.reduce((sum, l) => sum + l.unit, 0);

  // Multi-week discount applies once per week across the whole order — print,
  // digital, or both — as long as there's at least one billable placement.
  const hasBillable     = !!printAd || digitalLines.some(l => l.weeks > 0);
  const discountPerWeek = hasBillable ? getDiscountPerWeek(weeks) : 0;
  const totalSavings    = discountPerWeek * weeks;

  // Promo (flash sale) — stacks on top of both the multi-week discount and the
  // non-profit rate card. Gated to qualifying print sizes on a qualifying start
  // date, so it can't drive a small ad toward zero.
  const promoStatus  = getPromoStatus(form);
  const promoPerWeek = promoStatus.applies ? promoStatus.promo.discountPerWeek : 0;
  const promoSavings = promoPerWeek * weeks;

  const weeklyRate = printWeekly + digitalWeekly; // nominal full-week rate (display only)
  const grossCost  = printCost + digitalCost;     // before any discount
  const total      = Math.max(0, grossCost - totalSavings - promoSavings);
  const hasSkips   = digitalLines.some(l => l.skipped > 0);

  return {
    printBase, discountPerWeek, printWeekly, printCost,
    digitalWeekly, digitalCost, digitalLines,
    weeklyRate, totalSavings, grossCost, total, weeks, hasSkips,
    promo:        promoStatus.promo,
    promoApplies: promoStatus.applies,
    promoReason:  promoStatus.reason,
    promoPerWeek, promoSavings,
  };
}

// Ordering closes at 5:00 pm Hendersonville (Eastern) time on the cutoff day,
// whatever time zone or clock setting the advertiser's device has.
const CUTOFF_HOUR = 17;
const CUTOFF_TZ   = 'America/New_York';

const easternHour = (ms) => Number(new Intl.DateTimeFormat('en-US', {
  timeZone: CUTOFF_TZ, hour: 'numeric', hourCycle: 'h23',
}).format(new Date(ms)));

// The instant it is `hour`:00 on the calendar day of `date` in Eastern time.
// Eastern is UTC-4 in summer and UTC-5 in winter; take whichever lands on `hour`.
function easternInstant(date, hour) {
  const edt = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hour + 4);
  return new Date(easternHour(edt) === hour ? edt : edt + 60 * 60 * 1000);
}

// When ordering closes for a Wednesday issue:
//   regular issue   — the Friday before (5 days ahead) at 5 pm ET
//   Special Edition — the Wednesday a week before (7 days ahead) at 5 pm ET
// Creative has its own, later deadline (see getAdDue); this only stops new orders.
export function getOrderCutoff(issueDate) {
  const d = new Date(issueDate);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (getSpecialEdition(d) ? 7 : 5));
  return easternInstant(d, CUTOFF_HOUR);
}

export function isIssueOpen(issueDate, now = new Date()) {
  return now < getOrderCutoff(issueDate);
}

// The day ad files are due (end of day) for a Wednesday issue: the Monday
// before, unless a Special Edition sets its own earlier day in config.js.
export function getAdDue(issueDate) {
  const d = new Date(issueDate);
  d.setHours(0, 0, 0, 0);
  const se = getSpecialEdition(d);
  d.setDate(d.getDate() - ((se && se.adDueDaysBefore) || 2));
  return d;
}

// 'Wed, Nov 18, 5 pm ET' — when ordering for an issue closes.
export function formatOrderCutoff(issueDate) {
  const day = getOrderCutoff(issueDate).toLocaleDateString('en-US', {
    timeZone: CUTOFF_TZ, weekday: 'short', month: 'short', day: 'numeric',
  });
  return `${day}, ${CUTOFF_HOUR - 12} pm ET`;
}

// Returns an array of bookable Wednesday Date objects (local midnight).
// A closed issue is dropped from the list entirely rather than shown and
// blocked, so nobody can pay for a paper that has already closed. A Special
// Edition closes earlier than the weeks around it, so the list can skip it and
// still offer the regular issue after it.
export function getUpcomingWednesdays(count = 52) {
  const now = new Date();
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  // Start from next Wednesday, even if today is Wednesday.
  d.setDate(d.getDate() + ((3 - d.getDay() + 7) % 7 || 7));
  const result = [];
  while (result.length < count) {
    if (isIssueOpen(d, now)) result.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return result;
}

// The soonest issue an advertiser can still make the deadline for. Used as the
// pre-selected start date so the form always opens on a real, bookable Wednesday.
export function getFirstBookableWednesday() {
  return getUpcomingWednesdays(1)[0];
}

export function getRunSchedule(startDate, weeks) {
  if (!startDate) return [];
  const dates = [];
  const d = new Date(startDate);
  d.setHours(0, 0, 0, 0);
  for (let i = 0; i < weeks; i++) {
    dates.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return dates;
}

// Produces YYYY-MM-DD in local time (avoids UTC-offset shift from toISOString).
export function toDateInputValue(d) {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function formatDate(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

export function formatShortDate(date) {
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export function fmtDollars(n) {
  return `$${n.toLocaleString()}`;
}

// Returns the Special Edition object for a given Date, or null.
export function getSpecialEdition(date) {
  if (!date) return null;
  const dateStr = toDateInputValue(date);
  return SPECIAL_EDITIONS.find(se => se.date === dateStr) || null;
}

// Returns array of SE objects that fall within a run schedule.
export function getSpecialEditionsInSchedule(runSchedule) {
  return runSchedule.map(getSpecialEdition).filter(Boolean);
}
